import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { format } from "date-fns";
import {
  CalendarIcon,
  CheckCircle2,
  Clock,
  FileText,
  Loader2,
  Mail,
  MessageCircle,
  Phone,
  User,
} from "lucide-react";
import { toast } from "sonner";

import { Calendar } from "./ui/calendar";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";
import api, { formatApiErrorDetail } from "../lib/api";
import { WHATSAPP_LINK } from "../lib/constants";
import { buildWipayPayload, storePendingWipayPayment, WIPAY_CONFIG } from "../lib/wipay";

const APPOINTMENTS_STORAGE_KEY = "bdp_demo_appointments";
const DEFAULT_SERVICE = "General Appointment";
const WEEKDAY_SLOTS = ["09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00"];
const SATURDAY_SLOTS = ["09:00", "10:00", "11:00"];

function dateKey(date) {
  return date ? format(date, "yyyy-MM-dd") : "";
}

function isSameDay(a, b) {
  return a && b && dateKey(a) === dateKey(b);
}

function toSlotLabel(slot) {
  const [hourText] = slot.split(":");
  const hour = Number(hourText);
  const start = new Date(2026, 0, 1, hour, 0);
  const end = new Date(2026, 0, 1, hour + 1, 0);
  const options = { hour: "numeric", minute: "2-digit" };

  return `${start.toLocaleTimeString([], options)} - ${end.toLocaleTimeString([], options)}`;
}

function getStoredBookedSlots(selectedDate) {
  if (!selectedDate || typeof window === "undefined") return [];

  try {
    const appointments = JSON.parse(window.localStorage.getItem(APPOINTMENTS_STORAGE_KEY) || "[]");
    const selectedKey = dateKey(selectedDate);

    return appointments
      .filter((appointment) => appointment.preferred_date === selectedKey)
      .map((appointment) => appointment.preferred_time);
  } catch {
    return [];
  }
}

function getBaseSlots(selectedDate) {
  if (!selectedDate) return [];

  const day = selectedDate.getDay();
  if (day === 0) return [];
  return day === 6 ? SATURDAY_SLOTS : WEEKDAY_SLOTS;
}

function isPastSlot(selectedDate, slot) {
  if (!selectedDate) return false;

  const today = new Date();
  if (!isSameDay(selectedDate, today)) return false;

  const [hour, minute] = slot.split(":").map(Number);
  const slotDate = new Date(selectedDate);
  slotDate.setHours(hour, minute, 0, 0);

  return slotDate <= today;
}

export default function BookingForm({ variant = "page", onCompleted }) {
  const isPopup = variant === "popup";
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", problem: "" });
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [sessionBookedSlots, setSessionBookedSlots] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const selectedDateKey = dateKey(date);
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const slots = useMemo(() => {
    const storedBooked = getStoredBookedSlots(date);
    const sessionBooked = sessionBookedSlots
      .filter((slot) => slot.startsWith(`${selectedDateKey}|`))
      .map((slot) => slot.split("|")[1]);
    const booked = new Set([...storedBooked, ...sessionBooked]);

    return getBaseSlots(date).map((slot) => ({
      value: slot,
      label: toSlotLabel(slot),
      disabled: booked.has(slot) || isPastSlot(date, slot),
    }));
  }, [date, selectedDateKey, sessionBookedSlots]);

  const resetForm = () => {
    setDone(false);
    setForm({ name: "", email: "", phone: "", problem: "" });
    setDate(null);
    setTime("");
  };

  const onSubmit = async (event) => {
    event.preventDefault();

    if (!form.name || !form.email || !form.phone || !form.problem || !date || !time) {
      toast.error("Please complete all required fields.");
      return;
    }

    setSubmitting(true);
    let redirectingToWipay = false;

    try {
      const { data: appointment } = await api.post("/appointments", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        service: DEFAULT_SERVICE,
        preferred_date: selectedDateKey,
        preferred_time: time,
        notes: form.problem,
        status: "pending_payment",
        payment_status: "pending",
        payment_provider: "wipay",
        payment_amount: WIPAY_CONFIG.total,
        payment_currency: WIPAY_CONFIG.currency,
      });

      setSessionBookedSlots((current) => [...current, `${selectedDateKey}|${time}`]);
      toast.success("Appointment saved. Redirecting to WiPay...");
      onCompleted?.();
      redirectingToWipay = true;
      storePendingWipayPayment(buildWipayPayload({ appointment, form }));
      navigate("/payment/redirect");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to submit. Try again.");
    } finally {
      if (!redirectingToWipay) {
        setSubmitting(false);
      }
    }
  };

  const formContent = done ? (
    <div className={`${isPopup ? "p-0" : "card-elev p-10"} text-center`} data-testid="booking-success">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-paradise-green/15">
        <CheckCircle2 className="h-8 w-8 text-paradise-green" />
      </div>
      <h3 className="mt-6 text-3xl font-semibold text-paradise-navy">You're booked!</h3>
      <p className="mx-auto mt-3 max-w-md text-paradise-slate">
        Thanks for reaching out. A member of our team will confirm your appointment within a few
        business hours.
      </p>
      <button className="btn-ghost mt-7" onClick={resetForm} data-testid="booking-reset-btn">
        Book another
      </button>
    </div>
  ) : (
    <form
      onSubmit={onSubmit}
      className={`${isPopup ? "space-y-4 sm:space-y-5" : "card-elev space-y-5 p-7 sm:p-10"}`}
      data-testid="booking-form"
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <Label className="mb-2 flex items-center gap-1.5 text-[14px] font-semibold text-[#06386f] sm:text-[15px]">
            <User className="h-4 w-4" /> Your Name
          </Label>
          <Input
            value={form.name}
            onChange={(event) => update("name", event.target.value)}
            placeholder="John Doe"
            className="h-11 rounded-xl sm:h-12"
            data-testid="booking-name"
          />
        </div>
        <div>
          <Label className="mb-2 flex items-center gap-1.5 text-[14px] font-semibold text-[#06386f] sm:text-[15px]">
            <Mail className="h-4 w-4" /> Email Address
          </Label>
          <Input
            type="email"
            value={form.email}
            onChange={(event) => update("email", event.target.value)}
            placeholder="user@example.com"
            className="h-11 rounded-xl sm:h-12"
            data-testid="booking-email"
          />
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <Label className="mb-2 flex items-center gap-1.5 text-[14px] font-semibold text-[#06386f] sm:text-[15px]">
            <Phone className="h-4 w-4" /> Phone Number
          </Label>
          <Input
            value={form.phone}
            onChange={(event) => update("phone", event.target.value)}
            placeholder="(876) 000-0000"
            className="h-11 rounded-xl sm:h-12"
            data-testid="booking-phone"
          />
        </div>
        <div>
          <Label className="mb-2 flex items-center gap-1.5 text-[14px] font-semibold text-[#06386f] sm:text-[15px]">
            <CalendarIcon className="h-4 w-4" /> Date
          </Label>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="flex h-11 w-full items-center gap-2 rounded-xl border border-input bg-white px-4 text-left text-sm transition-colors hover:border-paradise-blue sm:h-12"
                data-testid="booking-date-trigger"
              >
                <CalendarIcon className="h-4 w-4 text-paradise-blue" />
                {date ? format(date, "PPP") : <span className="text-muted-foreground">Pick a date</span>}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto rounded-[10px] border border-[#e2e8f0] bg-white p-0 shadow-[0_18px_40px_rgba(15,23,42,0.14)]" align="start">
              <Calendar
                mode="single"
                selected={date}
                onSelect={(selectedDate) => {
                  setDate(selectedDate);
                  setTime("");
                }}
                disabled={(day) => day < new Date(new Date().setHours(0, 0, 0, 0)) || day.getDay() === 0}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div>
        <Label className="mb-2 flex items-center gap-1.5 text-[14px] font-semibold text-[#06386f] sm:text-[15px]">
          <FileText className="h-4 w-4" /> Your Problem
        </Label>
        <Textarea
          rows={isPopup ? 3 : 4}
          value={form.problem}
          onChange={(event) => update("problem", event.target.value)}
          placeholder="Tell us what is bothering you or what treatment you need."
          className="rounded-xl"
          data-testid="booking-problem"
        />
      </div>

      <div>
        <Label className="mb-2 flex items-center gap-1.5 text-[14px] font-semibold text-[#06386f] sm:text-[15px]">
          <Clock className="h-4 w-4" /> Time (slots)
        </Label>
        <Select value={time} onValueChange={setTime} disabled={!date || slots.every((slot) => slot.disabled)}>
          <SelectTrigger className="h-11 rounded-xl sm:h-12" data-testid="booking-time">
            <SelectValue
              placeholder={
                !date ? "Pick date first" : slots.every((slot) => slot.disabled) ? "No slots available" : "Select Time Slot"
              }
            />
          </SelectTrigger>
          <SelectContent>
            {slots.map((slot) => (
              <SelectItem
                key={slot.value}
                value={slot.value}
                disabled={slot.disabled}
                data-testid={`booking-time-option-${slot.value}`}
              >
                {slot.label}
                {slot.disabled ? " - Booked" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="mt-2 text-xs text-paradise-slate">
          Slots are divided into 1 hour intervals. Mon-Fri 9AM-5PM, every other Saturday 9AM-12PM.
        </p>
      </div>

      <button
        type="submit"
        disabled={submitting}
        aria-busy={submitting}
        className="btn-primary w-full justify-center rounded-xl text-base disabled:pointer-events-none disabled:opacity-75"
        data-testid="booking-submit-btn"
      >
        {submitting ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            Redirecting to WiPay...
          </>
        ) : (
            `Book Appointment`

          // `Book Appointment - Pay ${WIPAY_CONFIG.currency} ${WIPAY_CONFIG.total}`
        )}
      </button>
    </form>
  );

  if (isPopup) return formContent;

  return (
    <section id="book" className="relative overflow-hidden bg-paradise-mist py-20 lg:py-28" data-testid="booking-section">
      <div className="pointer-events-none absolute -right-32 top-10 h-[420px] w-[420px] rounded-full bg-paradise-yellow/20 blur-3xl" />
      <div className="container-x relative grid items-start gap-14 lg:grid-cols-12">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <div className="eyebrow mb-5">Online booking</div>
          <h2 className="font-display text-4xl font-semibold leading-[1.05] text-paradise-navy sm:text-5xl">
            Reserve your smile.
            <br />
            <span className="font-medium italic text-paradise-blue">Choose your 1 hour slot.</span>
          </h2>
          <p className="mt-5 text-2xl text-paradise-coral">Results from the first visit.</p>
          <p className="mt-5 text-lg text-paradise-slate">
            Or skip the form and message us on WhatsApp. We'll book you in directly.
          </p>

          <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" className="btn-whatsapp mt-7" data-testid="booking-whatsapp-btn">
            <MessageCircle className="h-5 w-5" /> Book on WhatsApp instead
          </a>

          <div className="mt-10 space-y-4">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white text-paradise-blue shadow-soft">
                <Clock className="h-4 w-4" />
              </div>
              <div>
                <div className="font-semibold text-paradise-navy">
                  Mon-Fri 9:00-5:00 | Every other Sat 9:00-12:00
                </div>
                <div className="text-sm text-paradise-slate">Closed Sundays</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white text-paradise-yellow shadow-soft">
                <CheckCircle2 className="h-4 w-4" />
              </div>
              <div>
                <div className="font-semibold text-paradise-navy">Quick response</div>
                <div className="text-sm text-paradise-slate">We confirm most appointments within the business day</div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="lg:col-span-7"
        >
          {formContent}
        </motion.div>
      </div>
    </section>
  );
}
